import { useCallback, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { useDemoData } from '@/features/demo/useDemoMode';
import { useSession } from '@/features/auth/hooks/useSession';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import './RemoveDemoDataButton.css';

/**
 * Demo-data control rendered as a row in the workspace-options menu (moved out of
 * the desktop header, #demo-move). Behaviour matches the original header control:
 *  - Demo hidden  → "Show demo data" → opens the explanatory confirm modal →
 *    "Show demo" loads/shows demo and hides the real data.
 *  - Demo showing → "Hide demo data" → one tap, reversible (no confirm).
 * Demo and real data are mutually-exclusive VIEWS — switching only filters, never
 * deletes (see useDemoMode.filterItems). The row label itself never changes shape
 * (busy feedback lives inside the modal); confirm modal reuses RemoveDemoDataButton.css.
 *
 * Team workspaces: the "Show demo data" row is hidden — demo data is a
 * solo-workspace-only feature (#team-member-onboarding). "Hide demo data" stays
 * available so an owner can dismiss pre-existing demo data seeded before the
 * workspace became a team.
 */
const rowStyle: CSSProperties = {
  width: '100%',
  /* Matches WorkspaceOptionsModal's btnStyle so this row lines up with the
     other account-menu items. */
  padding: '8px 8px',
  background: 'none',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  color: 'var(--color-text-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  textAlign: 'left',
};

export function DemoDataMenuItem({ onAfterAction }: { onAfterAction?: () => void }) {
  const { available, showingDemo, busy, hideDemo, showDemo } = useDemoData();
  const { selectedContainer } = useSession();
  const isTeamWorkspace = selectedContainer?.type === 'team';
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(() => setModalOpen(false), modalOpen);
  const [modalRef, handleOverlayClick, handleOverlayMouseDown] =
    useModalOverlayClick(() => setModalOpen(false), 30);

  const handleRowClick = useCallback(() => {
    if (busy) return;
    if (showingDemo) {
      // Reversible — hide on one tap, like the original control, then close menu.
      hideDemo();
      onAfterAction?.();
    } else {
      // Show path keeps the explanatory confirm modal.
      setModalOpen(true);
    }
  }, [busy, showingDemo, hideDemo, onAfterAction]);

  const confirmShow = useCallback(async () => {
    if (busy) return;
    setError(null);
    try {
      await showDemo();
      setModalOpen(false);
      onAfterAction?.();
    } catch (err) {
      console.error('[DemoDataMenuItem] show demo failed:', err);
      setError('Could not load demo data. Please try again.');
    }
  }, [busy, showDemo, onAfterAction]);

  if (!available) return null;

  // Team workspaces: suppress the "Show demo data" row — demo data is a
  // solo-workspace-only feature (#team-member-onboarding). The "Hide demo data"
  // row stays available so pre-existing demo data can still be dismissed.
  if (isTeamWorkspace && !showingDemo) return null;

  // The options menu lives in #modal-portal-root (max z-index); portal the confirm
  // there too so its overlay (z-index 1000000) renders ABOVE the menu (z-index 9999).
  const portalRoot =
    typeof document !== 'undefined'
      ? document.getElementById('modal-portal-root') ?? document.body
      : null;

  return (
    <>
      <button
        type="button"
        onClick={handleRowClick}
        style={rowStyle}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        title={showingDemo ? 'Hide demo data — show your real data' : 'Show sample demo data'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
        {showingDemo ? 'Hide demo data' : 'Demo data'}
      </button>
      {modalOpen && portalRoot && createPortal(
        <div
          className="remove-demo-modal-overlay"
          onMouseDown={handleOverlayMouseDown}
          onClick={handleOverlayClick}
        >
          <div
            ref={modalRef}
            className="remove-demo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="remove-demo-modal-header">
              <h2>Show demo data?</h2>
              <button
                className="remove-demo-modal-close"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
                disabled={busy}
              >
                &times;
              </button>
            </div>
            <div className="remove-demo-modal-body">
              <p>
                This fills the workspace with sample data (emails, events, tasks, contacts,
                places, notes) and hides your real data while you explore.
              </p>
              <p className="remove-demo-modal-hint">
                Nothing is deleted — choose <strong>Hide demo data</strong> anytime to switch
                back to your own data.
              </p>
              {error && <p className="remove-demo-modal-error">{error}</p>}
            </div>
            <div className="remove-demo-modal-actions">
              <button
                type="button"
                className="remove-demo-modal-cancel"
                onClick={() => setModalOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="remove-demo-modal-confirm"
                onClick={confirmShow}
                disabled={busy}
              >
                {busy ? 'Loading…' : 'Show demo'}
              </button>
            </div>
          </div>
        </div>,
        portalRoot,
      )}
    </>
  );
}

export default DemoDataMenuItem;
